import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { FontPreview } from '../components/FontPreview';
import { ScriptureInsight, downloadScripturePdf } from '../components/ScriptureInsight';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Store, 
  Type, 
  Image as ImageIcon,
  Loader2,
  Plus,
  Trash2,
  RefreshCw,
  ShieldCheck,
  Package,
  CreditCard,
  Search,
  User,
  Eye,
  EyeOff,
  BookOpen
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to truncate description to 1 sentence or 15 words max
const truncateDescription = (desc, maxWords = 15) => {
  if (!desc) return '';
  // Get first sentence
  const firstSentence = desc.split('.')[0]?.trim();
  if (!firstSentence) return '';
  
  const words = firstSentence.split(/\s+/);
  if (words.length <= maxWords) {
    return firstSentence + (firstSentence.endsWith('.') ? '' : '.');
  }
  return words.slice(0, maxWords).join(' ') + '...';
};

// Typeface categories
const typefaceCategories = [
  { value: 'sans_serif', label: 'Sans Serif (Clean & Modern)' },
  { value: 'serif', label: 'Serif (Classic & Elegant)' },
  { value: 'display', label: 'Display (Bold & Impactful)' },
  { value: 'script', label: 'Script (Handwritten & Flowing)' },
  { value: 'monospace', label: 'Monospace (Technical & Code)' },
  { value: 'decorative', label: 'Decorative (Unique & Artistic)' },
  { value: 'condensed', label: 'Condensed (Narrow & Space-Saving)' },
  { value: 'art_deco', label: 'Art Deco (Geometric & Glamorous)' },
  { value: 'wacky', label: 'Wacky (Playful & Quirky)' },
];

// Letter-space options
const letterSpaceOptions = [
  { value: '0px', label: '0px' },
  { value: '1px', label: '1px' },
  { value: '2px', label: '2px' },
  { value: '3px', label: '3px' },
  { value: '4px', label: '4px' },
  { value: '5px', label: '5px' },
  { value: '6px', label: '6px' },
  { value: '7px', label: '7px' },
  { value: '8px', label: '8px' },
  { value: '9px', label: '9px' },
  { value: '10px', label: '10px' },
];

// Decoration options
const decorationOptions = [
  { value: 'none', label: 'None' },
  { value: 'underline', label: 'Underline' },
  { value: 'line-through', label: 'Line-through' },
  { value: 'overline', label: 'Overline' },
];

const decorationStyleOptions = [
  { value: 'solid', label: 'Solid' },
  { value: 'dotted', label: 'Dotted' },
  { value: 'dashed', label: 'Dashed' },
  { value: 'wavy', label: 'Wavy' },
];

// Border styles - Gothic Border Ornaments
const borderStyles = [
  { value: 'none', label: 'No Border' },
  { value: 'cathedral', label: 'Cathedral Arch', description: 'Gothic pointed arches with stone tracery' },
  { value: 'illuminated', label: 'Illuminated Manuscript', description: 'Medieval book page borders with gold leaf corners' },
  { value: 'ironcross', label: 'Iron Cross', description: 'Wrought iron cross motifs and Celtic knots' },
  { value: 'gargoyle', label: 'Gargoyle Frame', description: 'Stone gargoyle corner pieces with Gothic spires' },
  { value: 'rosewindow', label: 'Rose Window', description: 'Stained glass circular rose window pattern' },
  { value: 'cryptvines', label: 'Crypt Vines', description: 'Dark twisted vines with thorns and Gothic flowers' },
  { value: 'sacredscript', label: 'Sacred Script', description: 'Ancient runic borders with religious symbols' },
];

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, token, user, loading: authLoading } = useAuth();
  
  // Generation state
  const [prompt, setPrompt] = useState('');
  const [category, setCategory] = useState('sans_serif');
  const [letterSpace, setLetterSpace] = useState('0px');
  const [decoration, setDecoration] = useState('none');
  const [decorationStyle, setDecorationStyle] = useState('solid');
  const [generating, setGenerating] = useState(false);
  const [generatedFont, setGeneratedFont] = useState(null);
  
  // Shop posting state
  const [selectedBorder, setSelectedBorder] = useState('none');
  const [selectedBorderColor, setSelectedBorderColor] = useState('#0A0A0A');
  const [postingType, setPostingType] = useState(null); // 'font' or 'poster'
  const [customTitle, setCustomTitle] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [shopCategory, setShopCategory] = useState('Uncategorized');
  const [shopCategories, setShopCategories] = useState(['Featured', 'New Arrivals', 'Best Sellers', 'Uncategorized']);
  const [newCategory, setNewCategory] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  
  // Fix credits state
  const [fixCreditsEmail, setFixCreditsEmail] = useState('');
  const [fixCreditsAmount, setFixCreditsAmount] = useState(50);
  const [fixingCredits, setFixingCredits] = useState(false);
  
  // User search state
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  
  // Admin fonts & shop items
  const [adminFonts, setAdminFonts] = useState([]);
  const [shopItems, setShopItems] = useState([]);
  const [loadingFonts, setLoadingFonts] = useState(true);
  const [loadingShop, setLoadingShop] = useState(true);
  const [refreshingInsightId, setRefreshingInsightId] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isAdmin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchAdminFonts();
    fetchShopItems();
    fetchShopCategories();
  }, [authLoading, isAuthenticated, isAdmin, navigate]);

  const fetchAdminFonts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/fonts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdminFonts(response.data.fonts || []);
    } catch (error) {
      console.error('Failed to fetch admin fonts:', error);
    } finally {
      setLoadingFonts(false);
    }
  };

  const fetchShopCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/shop/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShopCategories(response.data.categories || ['Featured', 'New Arrivals', 'Best Sellers', 'Uncategorized']);
    } catch (error) {
      console.error('Failed to fetch shop categories:', error);
    }
  };

  const handleAddCategory = () => {
    if (newCategory.trim() && !shopCategories.includes(newCategory.trim())) {
      const updatedCategories = [...shopCategories, newCategory.trim()].sort();
      setShopCategories(updatedCategories);
      setShopCategory(newCategory.trim());
      setNewCategory('');
      setShowNewCategoryInput(false);
      toast.success(`Category "${newCategory.trim()}" added`);
    }
  };

  const fetchShopItems = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/shop/items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShopItems(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch shop items:', error);
    } finally {
      setLoadingShop(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/fonts/generate`,
        {
          prompt: prompt.trim(),
          typeface_category: category,
          letter_space: letterSpace,
          decoration: decoration !== 'none' ? decoration : null,
          decoration_style: decoration !== 'none' ? decorationStyle : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedFont(response.data);
      setCustomTitle(response.data.name || '');
      setCustomDescription(truncateDescription(response.data.description) || '');
      toast.success('Font generated successfully!');
      
      // Refresh admin fonts
      fetchAdminFonts();
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate font');
    } finally {
      setGenerating(false);
    }
  };

  const handlePostToShop = async (type) => {
    if (!generatedFont) {
      toast.error('Generate a font first');
      return;
    }

    setPostingType(type);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/shop/items`,
        {
          font_id: generatedFont.id,
          item_type: type,
          border_style: selectedBorder,
          border_color: selectedBorderColor,
          title: customTitle || generatedFont.name,
          description: customDescription || generatedFont.description,
          shop_category: shopCategory
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${type === 'font' ? 'Typeface' : 'Digital Poster'} added to shop!`);
      fetchShopItems();
      fetchShopCategories(); // Refresh categories
    } catch (error) {
      console.error('Failed to post to shop:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to shop');
    } finally {
      setPostingType(null);
    }
  };

  const handleRemoveFromShop = async (itemId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/shop/items/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Item removed from shop');
      fetchShopItems();
    } catch (error) {
      console.error('Failed to remove item:', error);
      toast.error('Failed to remove item');
    }
  };

  const handleRefreshInsight = async (itemId) => {
    setRefreshingInsightId(itemId);
    try {
      await axios.post(
        `${API_URL}/api/admin/shop/items/${itemId}/scripture-insight`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Scripture Insight created. This item now has a PDF download.');
      fetchShopItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create Scripture Insight');
    } finally {
      setRefreshingInsightId(null);
    }
  };

  const handleToggleVisibility = async (itemId, currentVisibility) => {
    try {
      const response = await axios.patch(
        `${API_URL}/api/admin/shop/items/${itemId}/visibility`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchShopItems();
    } catch (error) {
      console.error('Failed to toggle visibility:', error);
      toast.error('Failed to toggle visibility');
    }
  };

  const handleFixCredits = async () => {
    if (!fixCreditsEmail || fixCreditsAmount <= 0) {
      toast.error('Please enter valid email and credits amount');
      return;
    }
    
    setFixingCredits(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/fix-credits`,
        {
          user_email: fixCreditsEmail,
          credits_to_add: fixCreditsAmount
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setFixCreditsEmail('');
      setFixCreditsAmount(50);
      // Refresh search results if we have any
      if (searchResults.length > 0) {
        handleSearchUsers();
      }
    } catch (error) {
      console.error('Failed to fix credits:', error);
      toast.error(error.response?.data?.detail || 'Failed to add credits');
    } finally {
      setFixingCredits(false);
    }
  };

  const handleSearchUsers = async () => {
    if (!userSearchQuery || userSearchQuery.length < 2) {
      toast.error('Please enter at least 2 characters to search');
      return;
    }
    
    setSearching(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/search-users?query=${encodeURIComponent(userSearchQuery)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSearchResults(response.data.users || []);
      if (response.data.users.length === 0) {
        toast.info('No users found matching your search');
      }
    } catch (error) {
      console.error('Failed to search users:', error);
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const selectUserForCredits = (email) => {
    setFixCreditsEmail(email);
    toast.success(`Selected: ${email}`);
  };

  const handlePostExistingFont = async (font, type) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/admin/shop/items`,
        {
          font_id: font.id,
          item_type: type,
          border_style: 'none',
          title: font.name,
          description: font.description
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${type === 'font' ? 'Typeface' : 'Digital Poster'} added to shop!`);
      fetchShopItems();
    } catch (error) {
      console.error('Failed to post to shop:', error);
      toast.error(error.response?.data?.detail || 'Failed to add to shop');
    }
  };

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#0D0507]">
      <Header />
      
      <main className="container mx-auto px-4 py-8 pt-24">
        {/* Admin Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#722F37] rounded-lg flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <div>
            <h1 className="text-2xl font-cinzel font-bold text-[#F4EFE6]">Admin Dashboard</h1>
            <p className="text-sm text-[#C2B49A] font-serif">Generate fonts and manage shop items</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Generator */}
          <div className="space-y-6">
            {/* Font Generator Card */}
            <div className="card-surface rounded-lg p-6">
              <h2 className="text-lg font-cinzel font-semibold mb-4 flex items-center gap-2 text-[#F4EFE6]">
                <Sparkles className="w-5 h-5 text-[#D4AF37]" />
                Generate Typeface
              </h2>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="prompt" className="text-[#F4EFE6] font-cinzel">Prompt</Label>
                  <Textarea
                    id="prompt"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe the font you want to create..."
                    className="mt-1 min-h-[100px] input-terminal rounded-lg"
                    data-testid="admin-prompt"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#F4EFE6] font-cinzel">Category</Label>
                    <Select value={category} onValueChange={setCategory}>
                      <SelectTrigger className="mt-1 input-terminal rounded-lg" data-testid="admin-category">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
                        {typefaceCategories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value} className="text-[#F4EFE6] focus:bg-[#220F13] focus:text-[#D4AF37] rounded-lg font-serif">
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-[#F4EFE6] font-cinzel">Letter-space</Label>
                    <Select value={letterSpace} onValueChange={setLetterSpace}>
                      <SelectTrigger className="mt-1 input-terminal rounded-lg" data-testid="admin-letterspace">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
                        {letterSpaceOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} className="text-[#F4EFE6] focus:bg-[#220F13] focus:text-[#D4AF37] rounded-lg font-serif">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-[#F4EFE6] font-cinzel">Decoration</Label>
                    <Select value={decoration} onValueChange={setDecoration}>
                      <SelectTrigger className="mt-1 input-terminal rounded-lg" data-testid="admin-decoration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
                        {decorationOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value} className="text-[#F4EFE6] focus:bg-[#220F13] focus:text-[#D4AF37] rounded-lg font-serif">
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {decoration !== 'none' && (
                    <div>
                      <Label className="text-[#F4EFE6] font-cinzel">Decoration Style</Label>
                      <Select value={decorationStyle} onValueChange={setDecorationStyle}>
                        <SelectTrigger className="mt-1 input-terminal rounded-lg" data-testid="admin-decoration-style">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
                          {decorationStyleOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className="text-[#F4EFE6] focus:bg-[#220F13] focus:text-[#D4AF37] rounded-lg font-serif">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                <Button
                  onClick={handleGenerate}
                  disabled={generating || !prompt.trim()}
                  className="w-full btn-primary rounded-lg"
                  data-testid="admin-generate-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Font
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Generated Font Preview & Post to Shop */}
            {generatedFont && (
              <div className="card-surface rounded-lg p-6">
                <h2 className="text-lg font-cinzel font-semibold mb-4 flex items-center gap-2 text-[#F4EFE6]">
                  <Store className="w-5 h-5 text-[#D4AF37]" />
                  Post to Shop
                </h2>
                
                {/* Preview */}
                <div className="mb-4">
                  <FontPreview 
                    font={generatedFont}
                    borderStyle={selectedBorder}
                    borderColor={selectedBorderColor}
                  />
                </div>

                {/* Scripture Insight */}
                <div className="mb-4">
                  <ScriptureInsight
                    meaning={generatedFont.scripture_meaning}
                    significance={generatedFont.scripture_significance}
                    previewText={generatedFont.preview_text}
                    onDownloadPdf={() => downloadScripturePdf(
                      `/api/scripture-pdf/font/${generatedFont.id}?border_style=${encodeURIComponent(selectedBorder)}&border_color=${encodeURIComponent(selectedBorderColor)}`,
                      token
                    )}
                  />
                </div>
                
                {/* Border Selection */}
                <div className="mb-4">
                  <Label className="text-[#F4EFE6] font-cinzel">Border Style (for poster)</Label>
                  <Select value={selectedBorder} onValueChange={setSelectedBorder}>
                    <SelectTrigger className="mt-1 input-terminal rounded-lg" data-testid="admin-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg max-h-80">
                      {borderStyles.map((b) => (
                        <SelectItem key={b.value} value={b.value} className="text-[#F4EFE6] focus:bg-[#220F13] focus:text-[#D4AF37] rounded-lg font-serif">
                          <div className="flex flex-col">
                            <span className="font-cinzel">{b.label}</span>
                            {b.description && (
                              <span className="text-xs text-[#C2B49A] font-serif">{b.description}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Border Color Selection - only show when border is selected */}
                {selectedBorder !== 'none' && (
                  <div className="mb-4">
                    <Label className="text-[#F4EFE6] font-cinzel">Border Color</Label>
                    <div className="flex items-center gap-3 mt-1">
                      <input
                        type="color"
                        value={selectedBorderColor}
                        onChange={(e) => setSelectedBorderColor(e.target.value)}
                        className="w-10 h-10 rounded-lg border border-[#D4AF37]/25 cursor-pointer p-1 bg-[#160A0D]"
                        data-testid="admin-border-color-picker"
                      />
                      <Input
                        type="text"
                        value={selectedBorderColor}
                        onChange={(e) => setSelectedBorderColor(e.target.value)}
                        placeholder="#000000"
                        className="flex-1 input-terminal uppercase rounded-lg"
                        data-testid="admin-border-color-input"
                      />
                    </div>
                  </div>
                )}
                
                {/* Custom Title & Description */}
                <div className="space-y-3 mb-4">
                  <div>
                    <Label className="text-[#F4EFE6] font-cinzel">Title</Label>
                    <Input
                      value={customTitle}
                      onChange={(e) => setCustomTitle(e.target.value)}
                      placeholder="Custom title for shop"
                      className="mt-1 input-terminal rounded-lg"
                    />
                  </div>
                  <div>
                    <Label className="text-[#F4EFE6] font-cinzel">Description</Label>
                    <Textarea
                      value={customDescription}
                      onChange={(e) => setCustomDescription(e.target.value)}
                      placeholder="Custom description for shop"
                      className="mt-1 input-terminal rounded-lg"
                    />
                  </div>
                  
                  {/* Shop Category */}
                  <div>
                    <Label className="flex items-center justify-between text-[#F4EFE6] font-cinzel">
                      <span>Shop Category</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowNewCategoryInput(!showNewCategoryInput)}
                        className="h-6 px-2 text-[#D4AF37] hover:text-[#C5A059] hover:bg-[#D4AF37]/10"
                        data-testid="add-category-btn"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </Label>
                    
                    {showNewCategoryInput ? (
                      <div className="flex gap-2 mt-1">
                        <Input
                          value={newCategory}
                          onChange={(e) => setNewCategory(e.target.value)}
                          placeholder="Enter new category name"
                          className="flex-1 input-terminal rounded-lg"
                          onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                          data-testid="new-category-input"
                        />
                        <Button
                          type="button"
                          onClick={handleAddCategory}
                          className="btn-primary rounded-lg"
                          disabled={!newCategory.trim()}
                          data-testid="save-category-btn"
                        >
                          Add
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowNewCategoryInput(false);
                            setNewCategory('');
                          }}
                          className="btn-secondary rounded-lg"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Select value={shopCategory} onValueChange={setShopCategory}>
                        <SelectTrigger className="mt-1 input-terminal rounded-lg" data-testid="shop-category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#160A0D] border border-[#D4AF37]/25 rounded-lg">
                          {shopCategories.map((cat) => (
                            <SelectItem key={cat} value={cat} className="text-[#F4EFE6] focus:bg-[#220F13] focus:text-[#D4AF37] rounded-lg font-serif">
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                
                {/* Post Buttons */}
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    onClick={() => handlePostToShop('font')}
                    disabled={postingType === 'font'}
                    className="bg-[#722F37] hover:bg-[#5A252C] text-white rounded-lg"
                    data-testid="post-font-btn"
                  >
                    {postingType === 'font' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Type className="w-4 h-4 mr-2" />
                    )}
                    Post as Typeface ($9.99)
                  </Button>
                  <Button
                    onClick={() => handlePostToShop('poster')}
                    disabled={postingType === 'poster'}
                    variant="outline"
                    className="btn-secondary rounded-lg"
                    data-testid="post-poster-btn"
                  >
                    {postingType === 'poster' ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ImageIcon className="w-4 h-4 mr-2" />
                    )}
                    Post as Poster ($6.99)
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Shop Items & Admin Fonts */}
          <div className="space-y-6">
            {/* Shop Items */}
            <div className="card-surface rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-cinzel font-semibold flex items-center gap-2 text-[#F4EFE6]">
                  <Package className="w-5 h-5 text-[#D4AF37]" />
                  Shop Items ({shopItems.length})
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchShopItems}
                  className="text-[#C2B49A] hover:text-[#D4AF37]"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              
              {loadingShop ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
                </div>
              ) : shopItems.length === 0 ? (
                <p className="text-center text-[#C2B49A] py-8 font-serif">No items in shop yet</p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {shopItems.map((item) => (
                    <div 
                      key={item.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        item.is_visible === false ? 'bg-[#160A0D]/50 opacity-60' : 'bg-[#160A0D]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {item.item_type === 'font' ? (
                          <Type className="w-5 h-5 text-[#D4AF37]" />
                        ) : (
                          <ImageIcon className="w-5 h-5 text-[#D4AF37]" />
                        )}
                        <div>
                          <p className="font-cinzel font-medium text-sm text-[#F4EFE6]">
                            {item.title}
                            {item.is_visible === false && (
                              <span className="ml-2 text-xs text-[#C2B49A]">(Hidden)</span>
                            )}
                          </p>
                          <p className="text-xs text-[#C2B49A] font-serif">
                            {item.item_type === 'font' ? 'Typeface' : 'Poster'} - ${item.price}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {!item.scripture_meaning && item.preview_text && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRefreshInsight(item.id)}
                            disabled={!!refreshingInsightId}
                            className="text-[#D4AF37] hover:text-[#F4EFE6] hover:bg-[#D4AF37]/10"
                            title="Create Scripture Insight"
                            data-testid={`refresh-insight-btn-${item.id}`}
                          >
                            {refreshingInsightId === item.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <BookOpen className="w-4 h-4" />
                            )}
                            <span className="ml-1 text-xs font-cinzel">Add Insight</span>
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleVisibility(item.id, item.is_visible)}
                          className={`${
                            item.is_visible === false 
                              ? 'text-[#C2B49A] hover:text-[#4ADE80] hover:bg-[#4ADE80]/10' 
                              : 'text-[#4ADE80] hover:text-[#C2B49A] hover:bg-[#C2B49A]/10'
                          }`}
                          title={item.is_visible === false ? 'Show in Shop' : 'Hide from Shop'}
                        >
                          {item.is_visible === false ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFromShop(item.id)}
                          className="text-[#9E2A2B] hover:text-[#FF6B6B] hover:bg-[#9E2A2B]/10"
                          title="Delete from Shop"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Admin Generated Fonts */}
            <div className="card-surface rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-cinzel font-semibold flex items-center gap-2 text-[#F4EFE6]">
                  <Type className="w-5 h-5 text-[#D4AF37]" />
                  My Generated Fonts ({adminFonts.length})
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchAdminFonts}
                  className="text-[#C2B49A] hover:text-[#D4AF37]"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
              
              {loadingFonts ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" />
                </div>
              ) : adminFonts.length === 0 ? (
                <p className="text-center text-[#C2B49A] py-8 font-serif">No fonts generated yet</p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {adminFonts.map((font) => (
                    <div 
                      key={font.id}
                      className="p-3 bg-[#160A0D] rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-cinzel font-medium text-sm text-[#F4EFE6]">{font.name}</p>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePostExistingFont(font, 'font')}
                            className="text-[#C2B49A] hover:bg-[#220F13] hover:text-[#D4AF37] text-xs px-2"
                            title="Post as Typeface"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Font
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePostExistingFont(font, 'poster')}
                            className="text-[#C2B49A] hover:bg-[#220F13] hover:text-[#D4AF37] text-xs px-2"
                            title="Post as Poster"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Poster
                          </Button>
                        </div>
                      </div>
                      <p 
                        className="text-sm text-[#C2B49A] truncate font-serif"
                        style={{
                          fontFamily: font.css_properties?.fontFamily || 'inherit',
                          fontWeight: font.style_properties?.weight || 400
                        }}
                      >
                        {font.preview_text?.slice(0, 50)}...
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* View Shop Button */}
            <Button
              onClick={() => navigate('/shop')}
              className="w-full btn-primary rounded-lg"
            >
              <Store className="w-4 h-4 mr-2" />
              View Public Shop
            </Button>
            
            {/* Search Users Section */}
            <div className="card-surface rounded-lg p-6 mt-6">
              <h2 className="text-lg font-cinzel font-semibold mb-4 flex items-center gap-2 text-[#F4EFE6]">
                <Search className="w-5 h-5 text-[#D4AF37]" />
                Search Users
              </h2>
              <p className="text-sm text-[#C2B49A] mb-4 font-serif">
                Find users by name or email to view their details
              </p>
              <div className="flex gap-2 mb-4">
                <Input
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  placeholder="Search by name or email..."
                  className="flex-1 input-terminal rounded-lg"
                  data-testid="user-search-input"
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchUsers()}
                />
                <Button
                  onClick={handleSearchUsers}
                  disabled={searching || userSearchQuery.length < 2}
                  className="bg-[#722F37] hover:bg-[#5A252C] text-white rounded-lg"
                  data-testid="user-search-btn"
                >
                  {searching ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </div>
              
              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {searchResults.map((user) => (
                    <div 
                      key={user.id}
                      className="p-3 bg-[#160A0D] rounded-lg border border-[#D4AF37]/20"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-[#C2B49A]" />
                          <div>
                            <p className="font-cinzel font-medium text-sm text-[#F4EFE6]">{user.name}</p>
                            <p className="text-xs text-[#C2B49A] font-serif">{user.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-1 rounded font-cinzel">
                            {user.generation_credits || 0} credits
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => selectUserForCredits(user.email)}
                            className="btn-secondary text-xs rounded-lg"
                          >
                            Select
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fix User Credits Section */}
            <div className="card-surface rounded-lg p-6 mt-6">
              <h2 className="text-lg font-cinzel font-semibold mb-4 flex items-center gap-2 text-[#F4EFE6]">
                <CreditCard className="w-5 h-5 text-[#D4AF37]" />
                Fix User Credits
              </h2>
              <p className="text-sm text-[#C2B49A] mb-4 font-serif">
                Manually add credits to a user&apos;s account (for fixing payment issues)
              </p>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="fix-email" className="text-[#F4EFE6] font-cinzel">User Email</Label>
                  <Input
                    id="fix-email"
                    type="email"
                    value={fixCreditsEmail}
                    onChange={(e) => setFixCreditsEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="mt-1 input-terminal rounded-lg"
                    data-testid="fix-credits-email"
                  />
                </div>
                <div>
                  <Label htmlFor="fix-amount" className="text-[#F4EFE6] font-cinzel">Credits to Add</Label>
                  <Input
                    id="fix-amount"
                    type="number"
                    min={1}
                    value={fixCreditsAmount}
                    onChange={(e) => setFixCreditsAmount(parseInt(e.target.value) || 0)}
                    className="mt-1 input-terminal rounded-lg"
                    data-testid="fix-credits-amount"
                  />
                </div>
                <Button
                  onClick={handleFixCredits}
                  disabled={fixingCredits || !fixCreditsEmail || fixCreditsAmount <= 0}
                  className="w-full bg-[#722F37] hover:bg-[#5A252C] text-white rounded-lg"
                  data-testid="fix-credits-btn"
                >
                  {fixingCredits ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Add Credits
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
